import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loading } from "../../components/Loading";
import { MainButton } from "../../components/MainButton";
import { ConfigType } from "../../contexts/AppConfigContext";
import { useAppConfig } from "../../hooks/useAppConfig";
import { usePluginspace } from "../../hooks/usePluginspace";
import "./index.css";
import Swal from "sweetalert2";
import api from "../../services/api";
import InputMask from "react-input-mask";
import { Checkbox } from "../../components/Checkbox";
import { telephoneNoMask } from "../../functions/mask";
import { getWeekDay, translateWeekday } from "../../functions/util";
import { ModalTerms } from "../../components/ModalTerms";

type ImageType = {
  id: string;
  url: string;
};

type HourType = {
  id: string;
  hour: string;
};

type DayType = {
  able: boolean;
  day:
    | "sunday"
    | "monday"
    | "tuesday"
    | "wednesday"
    | "thursday"
    | "friday"
    | "saturday";
  hours: HourType[];
};

type EnvironmentType = {
  id: string;
  name: string;
  seatsAvailable: number;
  tables: number;
};

type BookingConfigType = {
  id: string;
  able: boolean;
  dayConfirm: boolean;
  expiryTime: number;
  maxPeople: number;
  paymentConfirm: boolean;
  valuePaymentConfirm: number;
  days: DayType[];
  images: ImageType[];
  environment: EnvironmentType[];
  useTerms: boolean;
};

export function Booking() {
  const { pluginspace_id } = useParams();
  const { registerAccess, getToken, getThisPluginspace } = usePluginspace();
  const navigate = useNavigate();

  const { getAppConfig } = useAppConfig();
  const [config, setConfig] = useState<ConfigType | void>();
  const [loading, setLoading] = useState(true);
  const [bookingConfig, setBookingConfig] = useState<BookingConfigType | null>(
    null
  );

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dateBooking, setDateBooking] = useState("");
  const [hours, setHours] = useState([
    { value: 0, select: "Selecione um horário" },
  ]);
  const [hourId, setHourId] = useState(0);
  const [environmentId, setEnvironmentId] = useState(0);
  const [people, setPeople] = useState(0);
  const [phone, setPhone] = useState("");
  const [phoneFind, setPhoneFind] = useState("");
  const [maskTelephone, setMaskTelephone] = useState("(99) 99999-9999");
  const [maskTelephoneFind, setMaskTelephoneFind] = useState("(99) 99999-9999");
  const [acceptTerm, setAcceptTerm] = useState(false);
  const [term, setTerm] = useState("");
  const [openTerms, setOpenTerms] = useState(false);

  useEffect(() => {
    registerAccess("booking");
    init();
  }, []);

  async function init() {
    setLoading(true);
    localStorage.removeItem("bookingToPay");
    await getConfig();
    await getBookingConfig();
    await getTerms();
    setLoading(false);
  }

  async function getTerms() {
    await api
      .post(
        `/api/consent/filter`,
        {
          active: true,
        },
        {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        }
      )
      .then(async (response) => {
        if (response.data.data && response.data.data[0]) {
          setTerm(response.data.data[0].content);
        } else {
          setTerm("");
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }

  async function getBookingConfig() {
    await api
      .get("/api/queueReservation/bookingConfig", {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      })
      .then((response) => {
        const bookingResponse = response.data;
        if (
          bookingResponse &&
          bookingResponse.data &&
          bookingResponse.data[0]
        ) {
          const bookingConfigItem: BookingConfigType = bookingResponse.data[0];
          if (bookingConfigItem && bookingConfigItem.able) {
            setBookingConfig(bookingConfigItem);
          } else {
            Swal.fire({
              icon: "error",
              title: "Reserva não está habilitada!",
              text: "Loja não está com o esquema de reservas habilitada.",
            }).then(() => navigate(`/${pluginspace_id}`));
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Reserva não configurada!",
            text: "Loja não está com o esquema de reserva configurada.",
          }).then(() => navigate(`/${pluginspace_id}`));
        }
      })
      .catch((err) => {
        console.log(err);
        Swal.fire({
          icon: "error",
          title: "Reserva não configurada!",
          text: "Loja não está com o esquema de reservas configurada.",
        }).then(() => navigate(`/${pluginspace_id}`));
      });
  }

  async function goBooking() {
    if (people <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: `Precisa haver uma pessoa ou mais na reserva.`,
      });
      return;
    }

    if (!name) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: `É necessário adicionar um nome para reserva.`,
      });
      return;
    }

    if (!name.split(" ")[1]) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: `É necessário adicionar um sobrenome para reserva.`,
      });
      return;
    }

    if (phone.includes("_")) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: "Informe um número de telefone válido",
      });
      return;
    }

    if (
      !acceptTerm &&
      term !== "" &&
      term &&
      bookingConfig &&
      bookingConfig.useTerms
    ) {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "É necessário aceitar os termos para continuar.",
      });
      return;
    }
    if (bookingConfig?.maxPeople && people > bookingConfig.maxPeople) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        html: `Há mais pessoas do que o permitido na reserva<br>(Máximo ${bookingConfig.maxPeople})`,
      });
      return;
    }
    if (!hourId) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: `É necessário ter um horário para reserva`,
      });
      return;
    }
    if (!environmentId) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: `É necessário ter um ambiente para reserva`,
      });
      return;
    }

    if (!email.includes("@")) {
      Swal.fire({
        icon: "warning",
        title: "Atenção!",
        text: `E-mail inválido`,
      });
      return;
    }

    if (!bookingConfig?.id) {
      Swal.fire({
        icon: "error",
        title: "Erro!",
        text: `Erro ao encontrar configuração de reserva`,
      });
      return;
    }

    const hour = hours.find((h) => h.value === hourId);

    setLoading(true);
    const tk = await getToken();
    const pg = await getThisPluginspace(tk ? tk : "");
    if (pg && pg.gmt && hour?.select) {
      setLoading(true);
      const date = new Date(
        `${new Date().toString().split("GMT")[0]}${pg.gmt}`
      );
      if (
        date.getTime() >
        new Date(`${dateBooking} ${hour.select} GMT${pg.gmt}`).getTime()
      ) {
        Swal.fire({
          icon: "error",
          title: "Erro!",
          text: `Horário de reserva indisponível`,
        });
        setLoading(false);
        return;
      }
    }
    if (
      bookingConfig.paymentConfirm &&
      bookingConfig.valuePaymentConfirm &&
      bookingConfig.valuePaymentConfirm > 0
    ) {
      localStorage.setItem(
        "bookingToPay",
        JSON.stringify({
          date: dateBooking,
          bookingHourId: hourId,
          bookingEnvironmentId: environmentId,
          email,
          bookingConfigId: bookingConfig.id,
          people,
          name,
          phone: phone,
          hour: hour?.select,
        })
      );
      setLoading(false);
      navigate(`/${pluginspace_id}/booking/payment`);
    } else {
      const subscriptionStorage = localStorage.getItem(
        window.btoa("subscription_notification")
      );
      let subscription = null;
      if (
        subscriptionStorage &&
        window.atob(subscriptionStorage) &&
        JSON.parse(window.atob(subscriptionStorage))
      ) {
        subscription = JSON.parse(window.atob(subscriptionStorage));
      }

      await api
        .post(
          "/api/queueReservation/booking",
          {
            date: dateBooking,
            bookingHourId: hourId,
            bookingEnvironmentId: environmentId,
            email,
            bookingConfigId: bookingConfig.id,
            people,
            name,
            phone: telephoneNoMask(phone),
            subscription,
            urlNotification:
              window.location.href + "/" + telephoneNoMask(phone),
          },
          {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
            },
          }
        )
        .then((response) => {
          setLoading(false);
          const bookingResponse = response.data;
          if (bookingResponse.success) {
            Swal.fire({
              icon: "success",
              title: "Sucesso!",
              text: "Você realizou sua reserva.",
            }).then(() =>
              navigate(
                `/${pluginspace_id}/booking/success/${telephoneNoMask(phone)}`
              )
            );
          } else {
            Swal.fire({
              icon: "error",
              title: "Erro ao realizar reserva!",
              text: "Não foi possível realizar reserva.",
            }).then(async () => await init());
          }
        })
        .catch((err) => {
          setLoading(false);
          let title = "Erro ao realizar reserva!";
          let text = "Não foi possível realizar reserva.";
          if (
            err &&
            err.response &&
            err.response.data &&
            err.response.data.error
          ) {
            title = err.response.data.error;
          }
          if (
            err &&
            err.response &&
            err.response.data &&
            err.response.data.message
          ) {
            text = err.response.data.message;
          }
          Swal.fire({
            icon: "error",
            title,
            text,
          }).then(async () => await init());
        });
    }
  }

  async function getConfig() {
    const configStorage = await getAppConfig();
    setConfig(configStorage);
  }

  function changePhone(newPhone: string) {
    const phoneValidated = newPhone.replaceAll("_", "");
    if (phoneValidated.length >= 14 && phoneValidated[13] !== "_") {
      setMaskTelephone("(99) 99999-9999");
    } else {
      setMaskTelephone("(99) 9999-9999");
    }
  }

  function changePhoneFind(newPhone: string) {
    const phoneValidated = newPhone.replaceAll("_", "");
    if (phoneValidated.length >= 14 && phoneValidated[13] !== "_") {
      setMaskTelephone("(99) 99999-9999");
    } else {
      setMaskTelephone("(99) 9999-9999");
    }
  }

  function filterHour(hour: string, date: string) {
    const now = new Date().getTime();
    const timeToCompare = new Date(`${date} ${hour}`).getTime();
    if (now > timeToCompare) return false;
    return true;
  }

  function changeDate(data: string) {
    setHourId(0);
    const fullData = new Date(data);
    const week = getWeekDay(fullData.getDay());
    let newHours = [{ value: 0, select: "Selecione um horário" }];
    if (bookingConfig?.days) {
      const hoursFind = bookingConfig.days.find((day) => day.day === week);
      if (
        hoursFind &&
        hoursFind.able &&
        hoursFind.hours &&
        hoursFind.hours.length > 0
      ) {
        const hoursNext = hoursFind.hours
          .map((hour) => ({ value: parseInt(hour.id), select: hour.hour }))
          .sort((a, b) =>
            parseInt(a.select.split(":")[0]) > parseInt(b.select.split(":")[0])
              ? 1
              : parseInt(b.select.split(":")[0]) >
                parseInt(a.select.split(":")[0])
              ? -1
              : 0
          )
          .filter((hour) => filterHour(hour.select, data));
        setHours([...newHours, ...hoursNext]);
        setDateBooking(data);
        return;
      }
    }
    Swal.fire({
      icon: "warning",
      title: "Atenção!",
      text: `Não há horários disponíveis para ${translateWeekday(week)}.`,
    });
    setHours(newHours);
    setDateBooking("");
  }

  async function validateNumber() {
    try {
      setLoading(true);

      if (phoneFind.includes("_")) {
        Swal.fire({
          icon: "warning",
          title: "Atenção!",
          text: "Informe um número de telefone válido",
        });
        return;
      }

      await api
        .get(
          `/api/queueReservation/booking/${bookingConfig?.id}/${telephoneNoMask(
            phoneFind
          )}`,
          {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
            },
          }
        )
        .then((response) => {
          const bookingResponse = response.data;

          if (
            bookingResponse &&
            bookingResponse.data &&
            bookingResponse.data.length > 0
          ) {
            navigate(
              `/${pluginspace_id}/booking/${telephoneNoMask(phoneFind)}`
            );
          } else {
            Swal.fire({
              icon: "warning",
              title: "Atenção!",
              text: "Não foi possível encontrar reservas com esse número de telefone.",
            });
          }
        })
        .catch((err) => {
          let title = "Erro!";
          let text =
            "Não foi possível encontrar reservas com esse número de telefone.";
          if (
            err &&
            err.response &&
            err.response.data &&
            err.response.data.error
          ) {
            title = err.response.data.error;
          }
          if (
            err &&
            err.response &&
            err.response.data &&
            err.response.data.message
          ) {
            text = err.response.data.message;
          }
          Swal.fire({
            icon: "error",
            title,
            text,
          });
        });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="booking">
      {loading ? (
        <div style={{ width: "100%", height: "calc(100vh)" }}>
          <Loading type="spinningBubbles" color="#FFF" width={64} height={64} />
        </div>
      ) : (
        <div className="content-booking">
          {bookingConfig && (
            <div className="body-booking">
              {bookingConfig.images && bookingConfig.images[0] && (
                <div className="booking-image">
                  <img
                    src={bookingConfig.images[0].url}
                    alt="Imagem Reserva 1"
                  />
                </div>
              )}
              <div className="booking-form">
                <h2>Reserva</h2>
                <div className="multiple-input">
                  <div className="input" style={{ flex: 1 }}>
                    <label>Data</label>
                    <input
                      id="date-input"
                      type="date"
                      value={dateBooking || ""}
                      onChange={(event) => {
                        setDateBooking(event.target.value);
                        setHours([
                          { value: 0, select: "Selecione um horário" },
                        ]);
                      }}
                      onBlur={(event) => {
                        changeDate(event.target.value);
                      }}
                      min={new Date().toISOString().split("T")[0]}
                    />
                  </div>
                  <div className="input" style={{ flex: 1 }}>
                    <label>Horas</label>
                    <select
                      value={hourId}
                      onChange={(event) =>
                        setHourId(parseInt(event.target.value))
                      }
                    >
                      {hours.map((hour) => (
                        <option value={hour.value}>{hour.select}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="multiple-input">
                  <div className="input" style={{ flex: 1 }}>
                    <label>Ambiente</label>
                    {bookingConfig.environment &&
                    bookingConfig.environment.length > 0 ? (
                      <select
                        value={environmentId}
                        onChange={(event) =>
                          setEnvironmentId(parseInt(event.target.value))
                        }
                      >
                        <option value={0}>Escolha um ambiente</option>
                        {bookingConfig.environment.map((environment) => (
                          <option value={environment.id}>
                            {environment.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <select
                        value={environmentId}
                        onChange={(event) =>
                          setEnvironmentId(parseInt(event.target.value))
                        }
                      >
                        <option value={0}>Escolha um ambiente</option>
                      </select>
                    )}
                  </div>
                  <div className="input" style={{ flex: 1 }}>
                    <label>Número de pessoas</label>
                    <input
                      type="number"
                      value={people === 0 ? "" : people}
                      placeholder="0"
                      onChange={(event) =>
                        setPeople(parseInt(event.target.value))
                      }
                      max={bookingConfig.maxPeople}
                    />
                  </div>
                </div>
                <div className="input">
                  <label>Nome</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(event) => {
                      const isValidName = /^[a-zA-ZÀ-ÿ\s]*$/.test(
                        event.target.value
                      );
                      if (isValidName) setName(event.target.value);
                    }}
                  />
                </div>
                <div className="input">
                  <label>Celular</label>
                  <InputMask
                    mask={maskTelephone}
                    type="text"
                    value={phone}
                    // onBeforeInputCapture={(event) =>
                    //   changePhone(event.currentTarget.value)
                    // }
                    onChange={(event) => setPhone(event.target.value)}
                  />
                </div>
                <div className="input">
                  <label>Email</label>
                  <input
                    type="text"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </div>
                {/* { term !== '' && (
                                <div className="input-checkbox">
                                    <Checkbox name='accept' checked={acceptTerm} checkMark={() => setAcceptTerm(!acceptTerm)} />
                                    <label style={{cursor: 'pointer'}} onClick={() => setOpenTerms(true)} htmlFor="accept">Aceito os termos de uso de dados</label>
                                </div>
                            )} */}
                {acceptTerm || !bookingConfig.useTerms ? (
                  <div className="button-booking">
                    <MainButton
                      color={
                        config?.webappcolor.fontbutton
                          ? config.webappcolor.fontbutton
                          : "#636363"
                      }
                      backgroundColor={
                        config?.webappcolor.webappbutton
                          ? config.webappcolor.webappbutton
                          : "#EAEAEA"
                      }
                      text="Reservar →"
                      onClick={() => goBooking()}
                    />
                  </div>
                ) : (
                  <div className="button-booking">
                    <MainButton
                      color={
                        config?.webappcolor.fontbutton
                          ? config.webappcolor.fontbutton
                          : "#636363"
                      }
                      backgroundColor={
                        config?.webappcolor.webappbutton
                          ? config.webappcolor.webappbutton
                          : "#EAEAEA"
                      }
                      text="Termos de Consentimento"
                      onClick={() => setOpenTerms(true)}
                    />
                  </div>
                )}
                <div className="already-booking">
                  <h3>Já tem reserva?</h3>
                  <p>
                    Informe o seu telefone abaixo para visualizar as reservas já
                    confirmadas.
                  </p>
                  <div className="input">
                    <label>Celular</label>
                    <InputMask
                      mask={maskTelephoneFind}
                      type="text"
                      value={phoneFind}
                      // onBeforeInputCapture={(event) =>
                      //   changePhoneFind(event.currentTarget.value)
                      // }
                      onChange={(event) => setPhoneFind(event.target.value)}
                    />
                  </div>
                  <div className="button-booking">
                    <MainButton
                      color={
                        config?.webappcolor.fontbutton
                          ? config.webappcolor.fontbutton
                          : "#636363"
                      }
                      backgroundColor={
                        config?.webappcolor.webappbutton
                          ? config.webappcolor.webappbutton
                          : "#EAEAEA"
                      }
                      text="Procurar reserva"
                      onClick={() => validateNumber()}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {openTerms && (
            <ModalTerms
              isOpen={openTerms}
              setIsOpen={setOpenTerms}
              termContent={term}
              backgroundButton={
                config?.webappcolor.webappbutton &&
                config.webappcolor.webappbutton
              }
              fontButton={
                config?.webappcolor.fontbutton && config.webappcolor.fontbutton
              }
              acceptTerm={acceptTerm}
              setAcceptTerm={setAcceptTerm}
            />
          )}
        </div>
      )}
    </div>
  );
}
